import { IdCommand } from "commands/IdCommand";
import { AssignGameAsHomeworkCommand } from "commands/frankenstories/AssignGameAsHomeworkCommand";
import { AssignGameToClassCommand } from "commands/frankenstories/AssignGameToClassCommand";
import { CreateTemplateCommand } from "commands/frankenstories/CreateTemplateCommand";
import { UpdateTemplateCommand } from "commands/frankenstories/UpdateTemplateCommand";
import { sessionId } from "contexts/GlobalContext";
import { CodeResult } from "results/CodeResult";
import { CommandResult } from "results/CommandResult";
import { CompletedGamePlayerStateResult } from "results/frankenstories/CompletedGamePlayerStateResult";
import { CreateTemplateResult } from "results/frankenstories/CreateTemplateResult";
import { GenerateRandomStimulusResult } from "results/frankenstories/GenerateRandomStimulusResult";
import { GetAssignTemplateResult } from "results/frankenstories/GetAssignTemplateResult";
import { GetCreateTemplateResult } from "results/frankenstories/GetCreateTemplateResult";
import { GetGalleryResult } from "results/frankenstories/GetGalleryResult";
import { GetGameSettingsResult } from "results/frankenstories/GetGameSettingsResult";
import { GetGamesActiveHostResult } from "results/frankenstories/GetGamesActiveHostResult";
import { GetGamesResult } from "results/frankenstories/GetGamesResult";
import { GetHistoryResult } from "results/frankenstories/GetHistoryResult";
import { GetReportsResult } from "results/frankenstories/GetReportsResult";
import { GetTemplateResult } from "results/frankenstories/GetTemplateResult";
import { GetTemplateSettingsResult } from "results/frankenstories/GetTemplateSettingsResult";
import { GetTemplatesResult } from "results/frankenstories/GetTemplatesResult";
import { UploadImageResult } from "results/frankenstories/UploadImageResult";
import { ImageListResult } from "results/images/ImageListResult";
import { useApiService } from "services/ApiService";
import { GetTeacherHomeworkResult } from "results/frankenstories/GetTeacherHomeworkResult";
import { GetCreateGameResult } from "results/frankenstories/GetCreateGameResult";
import { RenameCommand } from "commands/RenameCommand";
import { FrankenstorySettingsDto } from "dtos/frankenstories/FrankenstorySettingsDto";
import { ImportTemplateCommand } from "commands/frankenstories/ImportTemplateCommand";
import { ImportTemplateFromHallOfFameCommand } from "commands/frankenstories/ImportTemplateFromHallOfFameCommand";
import { CreateFreePlayGameCommand } from "commands/frankenstories/CreateFreePlayGameCommand";
import { StartGameCommand } from "commands/frankenstories/StartGameCommand";
import { CodeCommand } from "commands/CodeCommand";

export const GamesApiService = () => {
    const { get, post, getHeaders, getUrl } = useApiService({ controller: "/games" });

    // get the user's current language
    const { language } = new Intl.Locale(navigator.language);

    return {
        uploadImage: async (file: File) => {
            const headers = getHeaders(false);

            const formData = new FormData();

            formData.append("file", file);

            const x = await fetch(getUrl("/upload-image"), {
                method: "POST",
                body: formData,
                headers,
            });

            return await (x.json() as Promise<CommandResult<UploadImageResult>>);
        },
        removeImage: (id: number) => post("/remove-image", { id }),
        getHistory: () => get<GetHistoryResult>(`/history/${sessionId}`),
        getPromptImages: () => get<ImageListResult>("/prompt-images"),
        getCompletedPlayerState: (code: string) =>
            get<CompletedGamePlayerStateResult>(`/completed-player-state/${code}/${sessionId}`),
        getRandom: (categoryId?: number, themeId?: number) =>
            get<GenerateRandomStimulusResult>(`/random?themeId=${themeId ?? ""}&categoryId=${categoryId ?? ""}`),
        getGameSettings: (code: string) => get<GetGameSettingsResult>(`/settings/${code}/${sessionId}`),
        getTemplateSettings: (id: number) => get<GetTemplateSettingsResult>(`/template/${id}/settings`),
        updateTemplate: (command: UpdateTemplateCommand) => post("/update-template", command),
        getCreateTemplate: (templateId: string | null) => {
            const suffix = templateId ? `?templateId=${templateId}` : "";
            return get<GetCreateTemplateResult>(`/create-template${suffix}`);
        },
        createTemplate: (command: CreateTemplateCommand) => post<CreateTemplateResult>("/create-template", command),
        renameTemplate: (command: RenameCommand) => post("/rename-template", command),
        getCreateGame: () => get<GetCreateGameResult>("/create-game"),
        deleteTemplate: (id: number) => post("/delete-template", { id }),
        getGames: () => get<GetGamesResult>(`/games/${sessionId}`),
        getGallery: (id: number) => get<GetGalleryResult>(`/gallery/${id}`),
        searchImages: (query: string) => get<GetGalleryResult>(`/search-images/${query}`),
        getTemplate: (id: number) => get<GetTemplateResult>(`/template/${id}`),
        getAssignTemplate: (id: number) => get<GetAssignTemplateResult>(`/template/${id}/assign`),
        assignTemplateToClass: (command: AssignGameToClassCommand) => post("/assign-to-class", command),
        assignTemplateAsHomework: (command: AssignGameAsHomeworkCommand) => post("/assign-as-homework", command),
        createQuickGameFromTemplate: (command: IdCommand) =>
            post<CodeResult>("/create-quick-game-from-template", command),
        favouriteImage: (id: number) => post("/favourite-image", { id }),
        unfavouriteImage: (id: number) => post("/unfavourite-image", { id }),
        createTemplateFromPrompt: (command: ImportTemplateCommand) =>
            post<CreateTemplateResult>("/create-template-from-prompt", command),
        createTemplateFromHallOfFame: (command: ImportTemplateFromHallOfFameCommand) =>
            post<CreateTemplateResult>("/create-template-from-hall-of-fame", command),
        getGamesActiveHost: (country: string) => get<GetGamesActiveHostResult>(`/active-host/${country}`),
        getTemplates: () => get<GetTemplatesResult>("/templates"),
        getReports: () => get<GetReportsResult>("/reports"),
        getTeacherHomework: () => get<GetTeacherHomeworkResult>("/teacher-homework"),
        setDefaultSettings: (command: FrankenstorySettingsDto) => post("/set-default-settings", command),
        createFreePlayGame: (command: CreateFreePlayGameCommand) =>
            post<CodeResult>("/create-free-play-game", { ...command, sessionId, language }),
        startGame: (command: StartGameCommand) => post("/start", { ...command, sessionId }),
        endGame: (command: CodeCommand) => post("/end", { ...command, sessionId }),
        deleteGame: (code: string) => post("/delete", { code, sessionId }),
    };
};
